import React, { ReactElement, useState } from 'react';
import { useAppStateContext } from 'src/AppState/AppState';
import { CAMPAIGN_LIST_ROOT } from 'src/domains/casino/utils/test-variables';
import { observer } from 'src/utils/mobx-react';
import { CampaignTable } from 'src/domains/casino/webview/bonus-engine/campaign-list/campaign-table/CampaignTable';
import { CampaignListState } from './CampaignList.state';
import { Button } from 'src/domains/common/components/button/button';
import { CampaignStore } from 'src/domains/casino/state/AppCasino/store/campaign-store/Campaign.store';
import { useCommon } from 'src/AppState/Common';
import { LoadingV2 } from 'src/components/Loading/LoadingV2';

export const CampaignList = observer('CampaignList', (): ReactElement | null => {
    const { routerBackoffice } = useAppStateContext();
    const common = useCommon();
    const [campaignListState] = useState(() => new CampaignListState(common, routerBackoffice));
    const campaignStore = CampaignStore.get(common);

    if (campaignStore.isLoading === true) {
        return (
            <LoadingV2
                dataTest={CAMPAIGN_LIST_ROOT}
                title='Loading campaign list...'
            />
        );
    }

    return (
        <div
            className='flex flex-col flex-auto max-h-screen gap-6 p-6 bg-white font-tertiary flex-nowrap'
            data-test={CAMPAIGN_LIST_ROOT}
        >
            <header className='flex items-center justify-between flex-[0_0_36px]'>
                <div className='flex flex-col gap-2'>
                    <h2
                        className='text-foreground m-0 text-[1rem] font-semibold leading-[1.75]'
                        data-test='page-title'
                    >
                        Campaign List
                    </h2>
                    <p
                        className='text-muted-foreground m-0 text-sm text-pretty max-w-2xl'
                        data-test='page-desc'
                    >
                        Create and manage campaigns, which allow you to set up the full promotion life cycle from
                        eligibility, triggering and rewarding conditions
                    </p>
                </div>

                <Button
                    icon='plus'
                    onClick={() =>
                        routerBackoffice.redirectToCampaign({
                            type: 'NEW',
                            view: 'general-details',
                            cloneId: null,
                        })
                    }
                    size='default'
                    variant='primary'
                    dataTest='new-campaign-button'
                    className='self-start'
                >
                    New Campaign
                </Button>
            </header>

            {campaignStore.hasZeroCampaigns === true ? (
                <article className='flex flex-col items-center justify-center flex-1 gap-4 text-center'>
                    <h3 className='m-0 text-sm text-foreground'>Start Your First Campaign</h3>
                    <p className='m-0 text-xs text-muted-foreground'>
                        It looks like you haven&#39;t created any campaigns yet.
                        <br />
                        To get started, click the &lsquo;New Campaign&rsquo; button in the top right corner.
                        <br />
                        Set up your first campaign and it will appear here.
                    </p>
                </article>
            ) : (
                <CampaignTable campaignListState={campaignListState} />
            )}
        </div>
    );
});
